import { Skeleton } from '@/components/ui';

/**
 * Loading placeholder for the snake market page body — the main-column card
 * (market header, chart, recent-windows strip) plus the desktop buy rail.
 * Grid-position classes match the live markup in {@link SnakeMarketPage} so
 * the skeleton occupies the same cells and nothing shifts on resolve.
 */
export function SnakeMarketBodySkeleton() {
  return (
    <>
      {/* Main column — header, chart, timeline strip (one bordered card on lg+). */}
      <div className="flex min-w-0 flex-col gap-5 xl:col-start-1 lg:gap-7 lg:rounded-[12px] lg:border-[1.5px] lg:border-stroke-soft lg:bg-surface lg:p-8 xl:p-10">
        <div className="flex flex-col gap-3 px-3.5 pt-3.5 lg:p-0">
          <div className="flex items-center gap-3">
            <Skeleton className="size-[50px] shrink-0 rounded-full lg:size-[64px]" />
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-6 w-48 rounded" />
              <Skeleton className="h-4 w-32 rounded" />
            </div>
          </div>
          {/* Price-to-beat / current-price stat bar. */}
          <Skeleton className="h-[44px] w-full rounded-[44px] lg:h-[53px]" />
        </div>

        <Skeleton className="h-[286px] w-full rounded-[12px] lg:h-[300px]" />

        {/* Recent-windows timeline pills. */}
        <div className="flex h-[44px] items-center justify-end gap-2 overflow-hidden pr-[14px]">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-[28px] w-[84px] shrink-0 rounded-pill" />
          ))}
        </div>
      </div>

      {/* Desktop buy rail — question recap + Set Amount + Up/Down buttons. */}
      <aside className="hidden md:block xl:sticky xl:top-20 xl:col-start-2 xl:row-start-2">
        <div className="flex flex-col items-center gap-4 rounded-card border border-border bg-surface p-4 lg:rounded-[16px] lg:border-[1.5px] lg:border-stroke-soft lg:p-6">
          <div className="hidden w-full items-center gap-2 lg:flex">
            <Skeleton className="size-[50px] shrink-0 rounded-full" />
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-5 w-40 rounded" />
              <Skeleton className="h-4 w-28 rounded" />
            </div>
          </div>
          <Skeleton className="h-5 w-28 self-start rounded" />
          <Skeleton className="h-[52px] w-full max-w-[280px] rounded-[43px]" />
          <div className="flex w-full gap-2">
            <Skeleton className="h-12 flex-1 rounded-pill" />
            <Skeleton className="h-12 flex-1 rounded-pill" />
          </div>
        </div>
      </aside>
    </>
  );
}

/**
 * Cold-load placeholder for the whole snake page (currencies not fetched yet):
 * the currency tab row plus {@link SnakeMarketBodySkeleton}, inside the same
 * responsive grid the loaded page uses.
 */
export function SnakeMarketPageSkeleton() {
  return (
    <div className="flex flex-col gap-5 xl:grid xl:grid-cols-[minmax(0,1fr)_380px] xl:items-start xl:gap-x-8 xl:gap-y-6 2xl:gap-x-[60px]">
      <div className="flex items-center gap-2 xl:col-span-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[28px] w-[92px] shrink-0 rounded-pill" />
        ))}
      </div>
      <SnakeMarketBodySkeleton />
    </div>
  );
}
